import * as fabricTypes from 'fabric'
import { fabric } from '~/utils/editor/fabric'
import { ObjectType } from '~/static/enums/object-type'
import { Icon as IconObject } from '~/static/icons'
import { EditorObject, LayoutContext, NamingContext } from '@/utils/editor/object.class'
import { FillType } from '~/static/enums/components/color-input'
import { ActualVersion, ActualVersionNumber } from '~/utils/editor/versioning/icon'
import { applyAlpha, assignNewColorAndAlpha, makeFabricGradient } from '~/utils/ColorHelper'
import { makeShadow } from '~/utils/editor/helpers/Helpers'

const DefaultWidth = 69.6
const DefaultHeight = 69.6

export class Icon extends EditorObject {
  svg: IconObject = { title: '', svg: '' }
  width: number = DefaultWidth
  height: number = DefaultHeight
  colorType: FillType = FillType.Solid
  color: FabricGradientOptions | string = 'rgba(0,0,0,1)'
  opacity: number = 100
  shadow: boolean = false
  shadowColor: string = 'rgba(0,0,0,1)'
  shadowOpacity: number = 100
  shadowX: number = 0
  shadowY: number = 4
  shadowBlur: number = 4

  /**
   * В версиях до v1.40.0 (включая) непропорциональные иконки, состоящие из нескольких path "вписывались" в рамку
   * квадрата, из одной - растягивались до квадрата. Начиная с версии v2.0.0 рамка стала подстраиваться под
   * непропорциональные иконки, и нам потребовался этот костыль, чтобы старые "вписанные" иконки сохраняли
   * предыдущее отображение. Ниже все использования voidsCrutch - это поддержка старой версии.
   */
  voidsCrutch: Partial<fabricTypes.GroupProps> = {}

  isColored: boolean = false

  constructor(props: ActualVersion) {
    super(ObjectType.Icon)
    this.assignProperties(props)
  }

  load(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.svg) {
        reject('Не задан параметр svg')
        return
      }
      fabric.loadSVGFromString(this.svg.svg)
        .then((result) => {
          this.setObject(fabric.util.groupSVGElements(
            result.objects.filter((item) => { return item !== null }) as fabricTypes.FabricObject[],
            result.options,
          ))
          if (Object.keys(this.voidsCrutch).length === 0) {
            if (this.object.type === 'group') {
              this.voidsCrutch = {
                width: result.options.width,
                height: result.options.height,
              }
            } else {
              this.voidsCrutch = {
                width: this.object.width,
                height: this.object.height,
              }
            }
          }
          this.isColored = this.checkIsColored()
          resolve()
        })
        .catch((error) => {
          reject(error ?? 'Svg loading failed: ' + this.svg.svg)
        })
    })
  }

  applyInitialLayout(context: LayoutContext): void {
    this.voidsCrutch = {
      width: this.object.width,
      height: this.object.height,
    }

    const standardWidth = this.width / context.zoom
    const standardHeight = this.height / context.zoom

    // calculateZoom здесь не подходит, так как надо не встроить, а растянуть до стандартного размера (то есть
    // если иконка меньше стандартного размера - ее scale должен быть меньше 1, чего не позволяет calculateZoom)
    const scale = Math.min(standardWidth / this.object.width, standardHeight / this.object.height)

    this.width = Math.round(this.object.width * scale)
    this.height = Math.round(this.object.height * scale)
  }

  applyDefaultName(context: NamingContext): void {
    if (!this.svg.title || this.svg.title.length === 0) {
      this.svg.title = 'Иконка ' + context.getNextIndex(ObjectType.Icon)
    }
  }

  async set(props: ActualVersion): Promise<void> {
    this.assignProperties(props)
    return this.applyProps()
  }

  protected assignProperties(props: ActualVersion): void {
    super.assignProperties(props)

    const color = assignNewColorAndAlpha(this.colorType, this.color, this.opacity, props.color, props.opacity)
    this.color = color.color
    this.opacity = color.opacity

    const shadowColor = assignNewColorAndAlpha(
      FillType.Solid,
      this.shadowColor,
      this.shadowOpacity,
      props.shadowColor,
      props.shadowOpacity,
    )
    this.shadowColor = <string>shadowColor.color
    this.shadowOpacity = shadowColor.opacity
  }

  protected applyProps(): Promise<void> {
    super.applyProps()
    this.object.set({
      width: this.voidsCrutch.width,
      height: this.voidsCrutch.height,
      scaleX: this.width / (this.voidsCrutch.width ?? DefaultWidth),
      scaleY: this.height / (this.voidsCrutch.height ?? DefaultHeight),
      shadow: makeShadow(this),
      ...(this.isColored ? {} : this.getColorOptions(this.object)),
    })

    // group только в том случае, если в svg несколько path, иначе тип объекта будет path
    if (!this.isColored && this.object.type === 'group') {
      const svgObjects = <fabricTypes.Group><unknown>this.object
      svgObjects.forEachObject((object, index) => {
        svgObjects._objects[index].set(this.getColorOptions(
          svgObjects._objects[index],
          -(object.left ?? DefaultWidth),
          -(object.top ?? DefaultHeight)
        ))
        svgObjects._objects[index].dirty = true
        svgObjects.dirty = true
      })
    }

    this.object.setCoords()
    return Promise.resolve()
  }

  afterModified(): boolean {
    this.width = this.getRealObjectWidth()
    this.height = this.getRealObjectHeight()
    return super.afterModified()
  }

  get name(): string {
    return this.svg.title ?? 'Иконка '
  }

  get bodyColour(): Rgba {
    if (typeof this.color === 'string') {
      return parseRgba(this.color)
    }
    if (this.color.colorStops === undefined) {
      return { r: 0, g: 0, b: 0, a: 1 }
    }
    let prevOffset = 0
    let maxSize = 0
    let maxColour = 'rgba(0,0,0,1)'
    for (const colorStop of this.color.colorStops.sort((a, b) => { return a.offset - b.offset })) {
      const size = colorStop.offset - prevOffset
      if (size > maxSize) {
        maxSize = size
        maxColour = colorStop.color
      }
      prevOffset = colorStop.offset
    }
    return parseRgba(maxColour)
  }

  checkIsColored(): boolean {
    return !this.isBlack(this.object.fill) ||
      !this.isBlack(this.object.stroke) ||
      (this.object.type === 'group' && (<fabricTypes.Group><unknown>this.object)._objects.findIndex((object) => {
        return !this.isBlack(object.fill) || !this.isBlack(object.stroke)
      }) >= 0)
  }

  isBlack(color: unknown): boolean {
    return color === undefined ||
      color === null ||
      color === '' ||
      color === 'currentColor' ||
      color === 'black' ||
      color === '#000' ||
      color === '#000000' ||
      color === 'rgb(0,0,0)' ||
      color === 'rgba(0,0,0,1)'
  }

  getColorOptions(
    object: fabricTypes.FabricObject,
    offsetX?: number,
    offsetY?: number,
  ): Partial<fabricTypes.FabricObjectProps> {
    if (
      (object.fill === undefined || object.fill === '') &&
      (object.stroke !== undefined && object.stroke !== '')
    ) {
      return {
        stroke: (typeof this.color === 'string') ?
          applyAlpha(this.color, this.opacity) :
          makeFabricGradient(
            this.voidsCrutch.width ?? DefaultWidth,
            this.voidsCrutch.height ?? DefaultHeight,
            this.color,
            offsetX,
            offsetY,
          ),
      }
    }
    return {
      fill: (typeof this.color === 'string') ?
        applyAlpha(this.color, this.opacity) :
        makeFabricGradient(
          this.voidsCrutch.width ?? DefaultWidth,
          this.voidsCrutch.height ?? DefaultHeight,
          this.color,
          offsetX,
          offsetY,
        ),
    }
  }

  toObject(): object {
    const data = super.toObject()
    data.version = ActualVersionNumber

    // if ('voidsCrutch' in data) {
    //   delete data.voidsCrutch
    // }

    return data
  }
}
